import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { BaselineFacts } from '../../components/baseline/BaselineFacts'
import { DailyHeadline } from '../../components/daily/DailyHeadline'
import type { FieldLean } from '../../components/field/quadrantField'
import { AboutReadings } from '../../components/home/AboutReadings'
import { BaselineRing } from '../../components/home/BaselineRing'
import { ConfidencePill } from '../../components/home/ConfidencePill'
import { FirstRunGuide } from '../../components/home/FirstRunGuide'
import { HomeDigest, type DigestLine } from '../../components/home/HomeDigest'
import { JourneyStrip, type JourneyStep } from '../../components/home/JourneyStrip'
import { LastSevenDays, type WeekDay } from '../../components/home/LastSevenDays'
import { NextReading } from '../../components/home/NextReading'
import { NoteCard } from '../../components/home/NoteCard'
import { Overview } from '../../components/home/Overview'
import { PendingUploadCard } from '../../components/home/PendingUploadCard'
import { pendingSentence } from '../../components/home/pendingSentence'
import { ProcessingBanner } from '../../components/home/ProcessingBanner'
import { QuadrantMap, type MapPoint } from '../../components/home/QuadrantMap'
import { QuadrantWord } from '../../components/home/QuadrantWord'
import { RecentRecordings } from '../../components/home/RecentRecordings'
import { RECENT_SHOWN, recentCaption } from '../../components/home/recentCaption'
import { WhatMoved, type ChipView } from '../../components/home/WhatMoved'
import { PageHeader } from '../../components/PageHeader'
import { Card, Icon, Notice, Pill, ProblemNotice, ResearchMarker, ResearchNotes, Tag } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { useHomeSummary } from '../../contexts/HomeSummaryContext'
import { useTheme, type ResolvedTheme } from '../../contexts/ThemeContext'
import {
  getBaselineStatus,
  getDailyStatus,
  listRecordings,
  type BaselineStatus,
  type RecordingRecord,
} from '../../lib/api'
import { readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { DEVICE_LABELS, baselineDevice, recordedDevices, type DeviceId } from '../../lib/devices'
import { RECORD_SECONDS } from '../../lib/capture/quality'
import {
  formatConfidence,
  formatMinutes,
  formatRelativeDay,
  formatSessionDate,
  formatWeekdayDate,
  formatWeekdayShort,
  lastSevenDays,
  localNoonOf,
} from '../../lib/format'
import { greetingFor } from '../../lib/greeting'
import { cornerWords, mirrorChips, mirrorStateMap, type HomeSummaryView, type NoteSource, type QuadrantToken } from '../../lib/home'
import { readPendingJob, type PendingJob } from '../../lib/pendingJob'
import { isNotFound } from '../../lib/problem'
import { CONDITION_WORDS, RECORDING_OUTCOME, bySessionDate } from '../../lib/recordings'
import type { Page } from '../../lib/usePagedList'
import { usePagedList } from '../../lib/usePagedList'
import { pickFromPages, type PickState } from '../../lib/usePickFromPages'
import { useResource, type Resource } from '../../lib/useResource'
import { useRetryLanding, type ArmLanding } from '../../lib/useRetryLanding'
import { AskAboutThis } from '../../components/assistant/AskAboutThis'
import { ASSISTANT_ENABLED } from '../../lib/features'

/**
 * Home — the overview a non-admin lands on, and the one screen that is never
 * blank.
 *
 * ── WHY THIS ROUTE EXISTS AT ALL ────────────────────────────────────────────
 *
 * Today is the readout of ONE recording and it is blank until one exists, so a
 * freshly approved account landed on a screen whose honest content was "you
 * have nothing". Home answers a different question — *where am I* — and Today
 * stays exactly what it is: the readout, at `/report` (named Today until 1.9.9).
 *
 * ── WHAT IT READS, AND WHAT THAT COSTS ──────────────────────────────────────
 *
 * One page of recordings, both baselines, and the Daily Status of the row it
 * picked: four GETs at most, two round trips deep, and no endpoint that did not
 * already exist. The baselines are the SECOND trip, beside the Daily Status:
 * which headband's baseline to read comes from page one (Sprint 1.9.2 S4). `autoLoadPages: 1` is written out rather than defaulted
 * because it is a decision: Today reads three pages hunting for a scored row
 * because it has nothing to show without one, and Home has a digest, two
 * baselines and a recent list to show regardless. The price is that a pick made
 * with a cursor still outstanding is not exhaustive, and §4.5 requires saying
 * so — which is the honesty line below.
 *
 * ── WHAT IT NEVER DOES ──────────────────────────────────────────────────────
 *
 * It mounts no score card and no Daily Status body, and it computes nothing
 * from a score. Every scientific string on it — the summary, the label roll-up,
 * the confidence, the disclaimer — is a payload field rendered verbatim. The
 * digest's lines are counts of rows and days and the payload's own flags, taken
 * HERE and handed down as finished sentences, which is why
 * `src/components/home/**` can hold presentation without becoming a second
 * place a measurement could be invented (Sprint 1.9.6 §1.3).
 */


/**
 * ── THREE MODES, ONE SCREEN (Sprint 1.9.9) ──────────────────────────────────
 *
 * The shell reads `/home` once (P6) and this screen takes its answer:
 *
 *  - `mirror` — the summary arrived. The mirror leads (R39): the note, what
 *    moved, the baseline, the next reading and the week, from `/home` alone;
 *    the 1.9.6 Home sits under it as "Your overview", minus the four things
 *    the mirror took over (the teaser and its Daily Status read, the digest's
 *    Record link, the pending-upload card, the disclaimer aside).
 *  - `absent` — a 404 `not_found`: an API from before `/home` (D33). The 1.9.6
 *    Home, as it was, and fully usable. Also the answer outside the provider,
 *    which is what every existing `Home.test.tsx` case renders.
 *  - `failed` — anything else. A notice that says so, above the 1.9.6 Home;
 *    never a silent fallback.
 *
 * `loading` holds the body until the summary has answered, so it paints once.
 */
type HomeMode = 'loading' | 'mirror' | 'absent' | 'failed'

export function Home() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  const { state: homeState, reload: reloadHome, refreshIfStale } = useHomeSummary()
  const mode: HomeMode = homeState.kind === 'ready' ? 'mirror' : homeState.kind

  // A console left open overnight reads again when Home is shown on a new day
  // (R55). A no-op until the first read has answered.
  useEffect(() => {
    refreshIfStale()
  }, [refreshIfStale])

  // Total rather than conditional, as Today's is: hooks cannot be called behind
  // an `if`, and this screen only renders inside RequireAuth, so the null branch
  // is a type obligation rather than a state a user reaches.
  const fetchPage = useCallback(
    (cursor: string | null, signal: AbortSignal): Promise<Page<RecordingRecord>> =>
      userId === null
        ? Promise.resolve({ items: [], next_cursor: null })
        : listRecordings(userId, { cursor }, { signal }),
    [userId],
  )

  // A revisit renders the last answers at once and reads them again quietly
  // (owner instruction, 2026-10-01; `lib/sessionCache.ts`). Every key names the
  // user; the cache is cleared on sign-out.
  const list = usePagedList<RecordingRecord>(fetchPage, {
    autoLoadPages: 1,
    cacheKey: userId === null ? null : `home:recordings:${userId}`,
  })
  const rows = list.items
  const pick = pickFromPages(list, (row) => row.status === 'scored', bySessionDate)
  const picked = pick.kind === 'picked' ? pick.item : null

  // The teaser is the old Home's; the mirror reads its reading from `/home`,
  // so it asks for no Daily Status at all (test #13), and nothing is asked for
  // until the summary has said which Home this is.
  const oldHome = mode === 'absent' || mode === 'failed'
  const loadStatus = useCallback(
    (signal: AbortSignal) =>
      picked === null || !oldHome ? Promise.resolve(null) : getDailyStatus(picked.recording_id, { signal }),
    [picked, oldHome],
  )
  const status = useResource(loadStatus, {
    cacheKey: userId === null || picked === null || !oldHome ? null : `home:status:${userId}:${picked.recording_id}`,
  })
  const result: DailyStatusResult | null =
    status.data === null ? null : readDailyStatus(status.data)

  // WHOSE BASELINES: the headband of the newest upload, as Baseline opens on
  // (`baselineDevice`), or the BrainBit Pro for an account with none. Each
  // headband keeps its own baseline, so a classic user shown the Pro's would
  // be shown someone else's empty history. `null` until page one has said.
  const baselineFor: DeviceId | null = list.isLoading
    ? null
    : baselineDevice(rows, recordedDevices(rows), null)

  // TWO READS, NEVER ONE. Eyes closed and eyes open are separate baselines
  // built from separate histories (§5.1); a single request with the answers
  // merged would be the client deciding they are one thing.
  const loadEc = useCallback(
    (signal: AbortSignal) =>
      userId === null || baselineFor === null
        ? Promise.resolve(null)
        : getBaselineStatus(userId, 'EC', { signal, deviceId: baselineFor }),
    [userId, baselineFor],
  )
  const loadEo = useCallback(
    (signal: AbortSignal) =>
      userId === null || baselineFor === null
        ? Promise.resolve(null)
        : getBaselineStatus(userId, 'EO', { signal, deviceId: baselineFor }),
    [userId, baselineFor],
  )
  const baselineKey = (condition: 'EC' | 'EO') =>
    userId === null || baselineFor === null ? null : `home:baseline:${userId}:${baselineFor}:${condition}`
  const ec = useResource(loadEc, { cacheKey: baselineKey('EC') })
  const eo = useResource(loadEo, { cacheKey: baselineKey('EO') })

  /**
   * Read ONCE, at mount, and never written.
   *
   * Record owns this pointer: it polls the job and it clears it. Home only
   * reports that one was started. Re-reading it on every render would also make
   * the card flicker when Record clears it in another tab, which is a thing
   * Home has no business reacting to.
   */
  const [pending] = useState(() => (userId === null ? null : readPendingJob(userId)))
  const actionDue = homeState.kind === 'ready'
    && homeState.summary.reading !== null
    && homeState.summary.reading.sessionDate !== homeState.summary.today
    && homeState.summary.baseline.EC.low_confidence_flag === false
    && homeState.summary.processing === null && pending === null

  /**
   * "Today", captured ONCE, like `pending` above.
   *
   * The digest counts the last seven days, the eyebrow names this one and the
   * greeting takes its part of day from it (R53), and all three must agree for
   * the life of the screen: asking the clock again on every render would let a
   * screen left open over midnight say one day in the eyebrow and count
   * another, or say "Good afternoon" while loading and "Good evening" once the
   * list arrived.
   */
  const [today] = useState(() => new Date())

  /**
   * THREE LANDINGS, ONE ELEMENT, AND WHY NOT ONE FLAG.
   *
   * `useRetryLanding` moves focus to the wrapper when the failure it watches
   * clears, because the "Try again" button that had focus unmounts with the
   * notice. One OR-ed flag over all three reads would strand focus on `<body>`
   * in the case that actually happens during an API blip: three reads fail, the
   * first retry succeeds, and the OR is still true — so nothing lands.
   */
  const ecFailed = ec.error != null && !isNotFound(ec.error)
  const eoFailed = eo.error != null && !isNotFound(eo.error)
  const [landList, armList] = useRetryLanding(list.error != null)
  const [landStatus, armStatus] = useRetryLanding(status.error != null)
  const [landBase, armBase] = useRetryLanding(ecFailed || eoFailed)
  // The summary's own failure lands the same way (critique #26).
  const homeFailed = homeState.kind === 'failed' && homeState.error != null
  const [landHome, armHome] = useRetryLanding(homeFailed)
  const landAll = useCallback(
    (el: HTMLDivElement | null) => {
      landList(el)
      landStatus(el)
      landBase(el)
      landHome(el)
    },
    [landList, landStatus, landBase, landHome],
  )

  /**
   * FIRST RUN IS THREE FACTS, AND EACH ONE IS LOAD-BEARING.
   *
   * `absent` (the list was read to the end and nothing was scored), no rows at
   * all, and no upload this browser started. A list that FAILED is `scanning`,
   * not `absent`, so a network failure can never be mistaken for an empty
   * account — which is the §4.5 failure this screen is most exposed to. A
   * stored pointer with no rows yet is an upload in flight, not a fresh start.
   */
  const firstRun = pick.kind === 'absent' && rows.length === 0 && pending === null

  /**
   * THE BODY PAINTS ONCE (D2).
   *
   * Everything the body shows has either arrived or failed before any of it is
   * drawn, so no "you are here" marker moves between steps while the reads land
   * and there is one loading state rather than five. First run is released as
   * soon as the list settles: the guide consults neither the baselines nor a
   * result.
   */
  const settled
    = mode !== 'loading'
    && !list.isLoading
    // The mirror asks for no Daily Status (its read resolves `null` at once),
    // so it never waits on one: waiting would put a loading frame in front of
    // a revisit whose every other answer is already cached.
    && (firstRun || (!ec.isLoading && !eo.isLoading && !(picked !== null && oldHome && status.isLoading)))

  const readable = result !== null && result.readable ? result : null
  // The disclaimer is owed exactly when a reading is on screen, and it is owed
  // the PAYLOAD'S sentence (R28, criterion 9). `LatestStatus` renders a reading
  // only in the `picked` arm and only when the payload is readable, so the
  // antecedent is the same pair of conditions here.
  const disclaimer
    = settled && pick.kind === 'picked' && status.error == null && readable !== null
      ? readable.disclaimer
      : null

  const bodyProps = {
    pick,
    rows,
    nextCursor: list.nextCursor,
    firstRun,
    pending,
    result,
    statusError: status.error,
    onRetryStatus: status.reload,
    armStatus,
    today,
    ec,
    eo,
    baselineFor,
    armBase,
  }

  return (
    <div className="stack stack--standard">
      <PageHeader
        // The title is the time-of-day greeting, and it never changes (R53,
        // which keeps R36's invariant): the account is loaded before this
        // route mounts and the part of day comes from `today`, captured once,
        // so loading, first run and every failure render the same heading.
        // Eleven live landing assertions read it, and a heading that becomes
        // something else after a fetch is the thing `settle.ts` exists to warn
        // about. The eyebrow is not hidden from assistive tech: R25 hid it
        // only for repeating the h1, and "Home" does not repeat a greeting.
        // The eyebrow names today, in full (D1 retired R30's "no weekday
        // formatter for an eyebrow"); first run keeps its welcome, which is
        // "Welcome" alone since 1.9.9 P5 (R53), as the mirror draws it.
        eyebrow={firstRun ? 'Welcome' : formatWeekdayDate(today)}
        title={greetingFor(account, today)}
        // The screen's one way to Record under the mirror (D6), shown once the
        // body has painted, so loading shows none — as it always has.
        actions={mode === 'mirror' && settled && !actionDue && pending === null
          && (homeState.kind !== 'ready' || homeState.summary.processing === null)
          ? <TakeReading /> : undefined}
        // Under the mirror there is no lead, as the concept draws it (fidelity
        // #6): the map says which reading this is. First run keeps its welcome.
        lead={
          // Only the old API's Home and first run have one, so the header never
          // shows a lead while /home loads and drops it when the mirror arrives.
          list.isLoading || (mode !== 'absent' && mode !== 'failed' && !firstRun)
            ? undefined
            : firstRun
              ? 'Welcome to BrainSpot. Put on your headband and record three quiet minutes on Record; it is scored here.'
              : newestUpload(rows, pending, today)
        }
      />

      {/* A read that failed PART WAY does not discard what it read, and the
          notice sits above the body so the body can still be a body. */}
      {list.error != null && (
        <ProblemNotice error={list.error} onRetry={list.reload} landing={armList}>
          Some of your history could not be loaded, so this may not be complete.
        </ProblemNotice>
      )}

      {/* D33: a summary that failed says so, above the old Home it falls back
          to. An unreadable answer has no Problem to show, so its own detail. */}
      {homeState.kind === 'failed' && (homeState.error != null ? (
        <ProblemNotice error={homeState.error} onRetry={reloadHome} landing={armHome}>
          Your summary could not be loaded.
        </ProblemNotice>
      ) : (
        <Notice tone="warn">{homeState.detail}</Notice>
      ))}
      {homeState.kind === 'ready' && homeState.reloadError != null && (
        <ProblemNotice error={homeState.reloadError} onRetry={reloadHome}>
          Your summary could not be refreshed, so this is how it looked when it last loaded.
        </ProblemNotice>
      )}

      {/* ALWAYS MOUNTED, loading included. A wrapper that appeared only once
          loading finished would not exist at the moment a retry succeeds, and
          the focus the three landings above are trying to place would go to
          `<body>` instead. */}
      <div className="retry-landing subgrid" ref={landAll} tabIndex={-1}>
        {settled ? (
          homeState.kind === 'ready' ? (
            <Mirror summary={homeState.summary} pending={pending} firstRun={firstRun}>
              <HomeBody {...bodyProps} mirror />
            </Mirror>
          ) : (
            <HomeBody {...bodyProps} mirror={false} />
          )
        ) : (
          <Card>
            <p className="loading-row" role="status">
              <span className="spinner" aria-hidden="true" /> Loading your overview…
            </p>
          </Card>
        )}
      </div>

      {/* History's element, not a component of Home's own, and never a literal:
          the sentence is the payload's (§1.5, criterion 9). */}
      {disclaimer !== null && (
        <aside className="disclaimer" aria-label="Disclaimer">
          {disclaimer}
        </aside>
      )}

      {/* R38 — ON THE PAGE, IN EVERY STATE. Below 1024px the shell's footer
          lives inside the drawer, so without this a phone reached the line
          only by opening the navigation. It is Home's, once: at 1024px and up
          the rail shows it too, which is the canvas's own composition. */}
      <p className="home-trust">Wellness and self-awareness tool. Not a medical device.</p>
    </div>
  )
}

interface BodyProps {
  pick: PickState<RecordingRecord>
  rows: readonly RecordingRecord[]
  nextCursor: string | null
  firstRun: boolean
  pending: ReturnType<typeof readPendingJob>
  result: DailyStatusResult | null
  statusError: unknown
  onRetryStatus: () => void
  armStatus: ArmLanding
  today: Date
  ec: Resource<BaselineStatus | null>
  eo: Resource<BaselineStatus | null>
  /** Which headband's baselines `ec` and `eo` are. Null only before page one settles. */
  baselineFor: DeviceId | null
  armBase: ArmLanding
  /** Under the mirror, which took the teaser, the Record link and the pending card (D6). */
  mirror: boolean
}

/**
 * Every `PickState`, with no hole — the shape Today's own switch argues for.
 *
 * Written as a `switch` with no `default` so the compiler, not a reviewer,
 * notices a fifth state. At `autoLoadPages: 1` `usePagedList` never sets
 * `budgetSpent`, so `capped` is unreachable here today and is still an arm: it
 * is one edit away from reachable, and the failure it would cause — a screen
 * that renders nothing at all — is the worst of the available ones, because it
 * looks like a screen with nothing to say.
 *
 * No arm returns `null`. Even with no rows and no pick, the digest and the
 * baselines are things this screen knows and can show.
 *
 * The journey strip is first run's alone (spec §1.6, AC1). It is onboarding: at
 * seventeen recordings it read `01 ✓ / 02 ✓ / 03 you are here` for good, and
 * the digest now says where things stand in facts rather than in steps.
 */
function HomeBody(props: BodyProps) {
  const { pick, rows, firstRun, pending, mirror } = props

  if (firstRun) {
    return (
      <>
        <JourneyStrip steps={FIRST_RUN_STEPS} />
        <FirstRunGuide action={mirror ? null : undefined} />
      </>
    )
  }

  // The digest is in every arm but first run, because it carries the one
  // Record link (D5). Its "not scored" line is only beside a picked result:
  // where nothing is scored, `UnscoredHistory` says so, and the digest would
  // say it twice. Under the mirror (1.9.9 D6) the link and the pending card
  // are the top section's, so the digest is drawn only when it has a line.
  const head = (withNotScored: boolean) => {
    const lines = digestLines(props, withNotScored)
    if (mirror) return lines.length === 0 ? null : <HomeDigest lines={lines} />
    return (
      <>
        {pending !== null && <PendingUploadCard pending={pending} />}
        <HomeDigest lines={lines} action={<RecordLink />} />
      </>
    )
  }
  const tail = rows.length === 0 ? null : <RecentRecordings rows={recent(props)} caption={caption(props)} />

  switch (pick.kind) {
    case 'picked':
      return (
        <>
          {head(true)}
          {mirror ? (
            <BaselinesCard {...props} />
          ) : (
            <div className="home-pair">
              <LatestStatus {...props} picked={pick.item} exhaustive={pick.exhaustive} />
              <BaselinesCard {...props} />
            </div>
          )}
          {tail}
        </>
      )
    case 'absent':
      // Read to the end. Either there is nothing at all — which is first run
      // above, or an upload in flight with no rows yet — or nothing scored.
      return rows.length === 0 ? (
        <>
          {head(false)}
          <BaselinesCard {...props} />
        </>
      ) : (
        <>
          {head(false)}
          <div className="home-pair">
            <UnscoredHistory count={rows.length} exhaustive />
            <BaselinesCard {...props} />
          </div>
          {tail}
        </>
      )
    case 'capped':
    case 'scanning':
      // Pages remain unread, for a budget or for a failure. Either way the
      // sentence is not "you have nothing".
      return rows.length === 0 ? (
        <>
          {head(false)}
          <BaselinesCard {...props} />
        </>
      ) : (
        <>
          {head(false)}
          <div className="home-pair">
            <UnscoredHistory count={rows.length} exhaustive={false} />
            <BaselinesCard {...props} />
          </div>
          {tail}
        </>
      )
  }
}

/**
 * The mirror's one way to Record (1.9.9 D6): "Take a reading · 3 min", the
 * length from `RECORD_SECONDS`, so the label follows the recording.
 */
function TakeReading() {
  return (
    <Link className="btn btn--primary" to="/record" data-testid="home-take-reading">
      <Icon name="record" />
      {`Take a reading · ${formatMinutes(RECORD_SECONDS)}`}
    </Link>
  )
}

/** The screen's one way to Record (D5 of the 1.9.1 brief), carried by the digest. */
function RecordLink() {
  return (
    <Link className="btn btn--primary" to="/record">
      <Icon name="record" />
      Record a session
    </Link>
  )
}

/**
 * ── THE DIGEST, COUNTED HERE AND NOWHERE ELSE ───────────────────────────────
 *
 * Spec §1.3: every count the digest states is taken in this file, which
 * `clientScience.test.ts` scans site by site, and handed to `HomeDigest` as a
 * finished sentence — `components/home/` never sees a row or a baseline
 * (`boundary.test.ts`). Each line is a count, a date or a payload flag, and each
 * is left out when its count is zero or its read failed (R37): a failed read is
 * "not known", never zero, and a zero about a person's week is not a fact
 * worth putting in front of them.
 *
 * The counting is `filter().length` and `Set.size`, which the scan does not
 * treat as sites. What it does see are the three comparisons that BOUND the
 * day count — two on the calendar dates of recordings and one on an upload
 * instant — and each has an ALLOWED row saying so.
 */
function digestLines(p: BodyProps, withNotScored: boolean): DigestLine[] {
  return [
    daysRecorded(p),
    ...baselineLines('EC', p.ec),
    ...baselineLines('EO', p.eo),
    withNotScored ? notScored(p) : null,
  ].filter((line): line is DigestLine => line !== null)
}

/** The outcomes a finished recording has. Anything else was never written (§1.5) and is not counted. */
const FINISHED: ReadonlySet<string> = new Set(['scored', 'rejected', 'empty', 'failed'])

/**
 * "You recorded on 5 of the last 7 days." — distinct recording days in the
 * last seven local calendar days, over page one.
 *
 * Stated plainly only when page one provably holds every recording in that
 * window: there is no further page, or its oldest upload is older than
 * `coveredBefore` (every upload comes after its recording, so nothing on a
 * later page can be dated inside the window). Otherwise it says which rows it
 * counted. Never a streak: a count of days, not a run of them.
 */
function daysRecorded({ rows, nextCursor, today }: BodyProps): DigestLine | null {
  // A failed list is not a zero: at one page a failed read leaves no rows, so
  // there is no day to count and the line is left out, as every zero is.
  const span = lastSevenDays(today)
  const days = new Set(
    rows
      .filter((row) => FINISHED.has(row.status))
      .filter((row) => row.session_date >= span.since && row.session_date <= span.today)
      .map((row) => row.session_date),
  ).size
  if (days === 0) return null
  const oldest = rows.at(-1)
  const covered = nextCursor === null
    || (oldest !== undefined && Date.parse(oldest.created_at) < Date.parse(span.coveredBefore))
  const count = <b>{days} of the last 7 days</b>
  return {
    key: 'days',
    body: covered ? (
      <>You recorded on {count}.</>
    ) : (
      <>
        In your {rows.length} most recently uploaded {rows.length === 1 ? 'recording' : 'recordings'},
        you recorded on {count}.
      </>
    ),
  }
}

/**
 * The countdown, if the baseline endpoint carries it.
 *
 * `days_confirmed_remaining` reaches `BaselineStatus` in Sprint 1.9.5 §2.3; until
 * then it is not in the generated types, so it is read structurally — the shape
 * `trend.ts` uses for `scaleBaseline` — and compiles on both sides of that
 * change. It is never derived: `min_baseline_sessions − n_distinct_days` would
 * be a client threshold (the product law), so an absent field drops the line.
 */
function daysConfirmedRemaining(status: BaselineStatus): number | null {
  const n: unknown = (status as BaselineStatus & { days_confirmed_remaining?: unknown }).days_confirmed_remaining
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/**
 * One line per condition, never one for both: eyes closed and eyes open are
 * separate baselines (§5.1).
 *
 * "Confirmed" is `low_confidence_flag === false` on a baseline that has
 * sessions — the package's own definition
 * (`eeg_tracking/baseline_store.py:304`: the flag is `n_days <
 * min_baseline_sessions`), which the 1.9.5 countdown shares (R37). A failed read
 * and a baseline that does not exist say nothing here: the row beneath already
 * says which it is.
 */
function baselineLines(condition: 'EC' | 'EO', r: Resource<BaselineStatus | null>): DigestLine[] {
  if (r.error != null || r.data == null || r.data.n_sessions === 0) return []
  const words = CONDITION_WORDS[condition]
  // `=== false` and `=== true`, never truthiness: a flag that is not a boolean
  // is a state the payload did not state, and null is never "confirmed".
  if (r.data.low_confidence_flag === false) {
    const days = r.data.n_distinct_days
    return [{
      key: `confirmed-${condition}`,
      body: (
        <>
          Your {words.toLowerCase()} baseline is confirmed, with{' '}
          <b>{days} separate {days === 1 ? 'day' : 'days'}</b> behind it.
        </>
      ),
    }]
  }
  if (r.data.low_confidence_flag !== true) return []
  const remaining = daysConfirmedRemaining(r.data)
  if (remaining === null || remaining === 0) return []
  return [{
    key: `held-low-${condition}`,
    tone: 'amber',
    // The countdown's words are the ones Today's baseline card already ships
    // (`DailyStatusView.tsx`), so the same fact reads the same on both screens.
    body: (
      <>
        {words} is building. <b>{remaining} more separate {remaining === 1 ? 'day' : 'days'}</b> to a
        confirmed baseline.
      </>
    ),
  }]
}

/** The outcomes that are not a score, in the order the digest names them. */
const NOT_SCORED = ['rejected', 'empty', 'failed'] as const

/**
 * "2 of your 6 recordings were not scored (1 Rejected, 1 No signal recorded)."
 *
 * "Not scored", not "produced no result": a rejected recording HAS a stored
 * result that was not used (`RECORDING_OUTCOME`), and the words in brackets are
 * the status pills' own. Scoped to the rows read, because one page is all Home
 * reads (R29).
 */
function notScored({ rows, nextCursor }: BodyProps): DigestLine | null {
  const counts = NOT_SCORED
    .map((status) => [status, rows.filter((row) => row.status === status).length] as const)
    .filter(([, n]) => n !== 0)
  const total = rows.filter((row) => (NOT_SCORED as readonly string[]).includes(row.status)).length
  if (total === 0) return null
  const breakdown = counts.map(([status, n]) => `${n} ${RECORDING_OUTCOME[status]?.label ?? status}`).join(', ')
  const noun = rows.length === 1 ? 'recording' : 'recordings'
  const of = nextCursor === null ? `${rows.length} ${noun}` : `${rows.length} most recently uploaded ${noun}`
  return {
    key: 'not-scored',
    body: (
      <>
        <b>{total} of your {of}</b> {total === 1 ? 'was' : 'were'} not scored ({breakdown}).
      </>
    ),
  }
}

/** Page one's first rows, in list order (D4). */
function recent({ rows }: BodyProps): readonly RecordingRecord[] {
  return rows.slice(0, RECENT_SHOWN)
}

/** The truncation sentence, or `null`: see `recentCaption`. */
function caption({ rows, nextCursor }: BodyProps): string | null {
  return recentCaption(rows.length, nextCursor)
}

/** A baseline with history behind it. */
function baselineStarted(r: Resource<BaselineStatus | null>): boolean {
  return r.error == null && r.data != null && r.data.n_sessions !== 0
}

/**
 * First run's three steps. Constant, because first run is three facts and all
 * of them are "not yet": no rows, the list read to the end, nothing pending. The
 * strip shows on first run only (§1.6), so the arms that read a result or a
 * baseline — and the "not known yet" arm a failed read needed — went with the
 * states that no longer draw it. `JourneyStrip` keeps all three states.
 */
const FIRST_RUN_STEPS: readonly [JourneyStep, JourneyStep, JourneyStep] = [
  {
    label: '01 · Record',
    state: 'todo',
    title: 'No recordings yet',
    body: 'Nothing is measured until you record something.',
  },
  {
    label: '02 · Baseline',
    state: 'todo',
    title: 'Not started',
    body: 'A baseline is built from your own scored recordings for one condition.',
  },
  {
    label: '03 · Daily status',
    state: 'todo',
    title: 'No scored result yet',
    body: 'Appears on Report once a session has been uploaded and scored.',
  },
]

/**
 * The lead: when the newest recording REACHED BrainSpot, and which condition.
 *
 * ── THE LABEL AND THE INSTANT IT NAMES HAVE TO AGREE (LAW-2) ────────────────
 *
 * `rows[0]` is the newest arrival by construction — the list pages newest-first
 * by `created_at` (D4) — so the instant this names is `created_at`: when the
 * recording reached BrainSpot. The sentence says exactly that. It may not say
 * "upload" (V1-INTEGRATION step 3): a session recorded in the browser reaches
 * BrainSpot without the person uploading anything, and the lead has to be true
 * on both paths. Nor may it say "your newest recording": one page ordered by
 * arrival CANNOT know the newest RECORDING: a session recorded on the 1st and uploaded on the 12th is `rows[0]`
 * while one recorded on the 11th sits behind it. That was review finding LAW-2
 * at 1.9.1 S5, on the journey's step 01; the strip is first run's now, and the
 * rule moved here with the only sentence left that names an arrival. The
 * canvas's "Your last measurement … and it scored" is the claim it forbids, and
 * the outcome is the first Recent-recordings pill, one card down.
 *
 * Nothing at all while an upload is pending: the job's row does not exist yet,
 * so the lead would name the one before it while the card
 * below says another was just started. Nothing on a failed list, either: at one
 * page a failed read leaves no rows, and the notice says what went wrong.
 */
function newestUpload(
  rows: readonly RecordingRecord[],
  pending: ReturnType<typeof readPendingJob>,
  today: Date,
): string | undefined {
  const newest = rows.at(0)
  if (newest === undefined || pending !== null) return undefined
  const when = formatRelativeDay(newest.created_at, today)
  if (when === null) return undefined
  return `Your latest recording to reach BrainSpot: ${when} — ${CONDITION_WORDS[newest.condition].toLowerCase()}.`
}

/**
 * The latest Daily Status, as a teaser.
 *
 * The payload's `summary` and label roll-up come from `DailyHeadline`, which is
 * the same component Today mounts inside the body — so the sentence a person
 * reads here and the sentence they read there are one string, not two. No score
 * card, no rings, no rows: this says what the day was called and how confident
 * the reading is, and Today is one click away for the rest.
 *
 * The confidence is the payload's number, written as the payload wrote it — not
 * a percentage, not rounded, and `null` rendered as an absence rather than as
 * zero. Its tone is keyed on the FLAG, never on the number, and "Held low" is
 * Baseline's phrase rather than a second vocabulary for the same fact.
 */
function LatestStatus({
  picked,
  exhaustive,
  result,
  statusError,
  onRetryStatus,
  armStatus,
}: BodyProps & { picked: RecordingRecord; exhaustive: boolean }) {
  if (statusError != null) {
    return (
      <ProblemNotice error={statusError} onRetry={onRetryStatus} landing={armStatus}>
        This result could not be loaded.
      </ProblemNotice>
    )
  }

  if (result === null) return null

  // §1.7 — an unusable payload gets a clear unavailable state, never a
  // half-rendered readout.
  if (!result.readable) return <Notice tone="warn">{result.detail}</Notice>

  return (
    <Card
      active
      eyebrow="Latest daily status"
      meta={
        <>
          {CONDITION_WORDS[picked.condition]}{' '}
          <span className="mono-cell">{picked.condition}</span> ·{' '}
          {formatSessionDate(picked.session_date)}
        </>
      }
    >
      <div className="home-status">
        <DailyHeadline status={result} />
      </div>
      {/* Shown only while pages remain unread, and phrased as what it is: the
          latest one FOUND, not the latest one there is (§4.5). */}
      {!exhaustive && (
        <p className="home-note">
          This is the most recent result in the pages read so far. Older pages of your
          history have not been searched.
        </p>
      )}
      <div className="home-status__foot">
        {formatConfidence(result.confidence) === null ? (
          <Pill>Confidence · Not available for this session</Pill>
        ) : (
          <Pill tone={result.lowConfidence ? 'amber' : 'green'} dot>
            Confidence {formatConfidence(result.confidence)}
            {result.lowConfidence ? ' · Held low' : ''}
          </Pill>
        )}
        <Link className="btn btn--ghost btn--sm" to="/report">
          See the full report
        </Link>
        {ASSISTANT_ENABLED && <AskAboutThis />}
      </div>
    </Card>
  )
}

/**
 * Both baselines, on their own rows.
 *
 * Two `BaselineFacts` fed by two resources, each field passed straight through.
 * They are never merged into one sentence and never summed: separate histories,
 * separate answers, and one failing says so on its own row while the other
 * keeps its counts.
 */
function BaselinesCard({ ec, eo, baselineFor, armBase }: BodyProps) {
  const started = baselineStarted(ec) || baselineStarted(eo)
  return (
    <Card
      className="span-12"
      eyebrow="Your baselines"
      // WHICH HEADBAND'S (Sprint 1.9.2 S4): each keeps its own, and Baseline is
      // where someone who has recorded with both chooses between them.
      meta={baselineFor === null ? undefined : DEVICE_LABELS[baselineFor]}
      head={
        <Link className="btn btn--ghost btn--sm" to="/baseline">
          Open Baseline
        </Link>
      }
    >
      <div>
        <BaselineFacts
          condition="EC"
          status={ec.data}
          isLoading={ec.isLoading}
          error={ec.error}
          onRetry={ec.reload}
          landing={armBase}
          variant="compact"
        />
        <BaselineFacts
          condition="EO"
          status={eo.data}
          isLoading={eo.isLoading}
          error={eo.error}
          onRetry={eo.reload}
          landing={armBase}
          variant="compact"
        />
      </div>
      {/* Baseline's own sentence, shown only when there is a count it could be
          explaining. Two numbers that legitimately disagree look like a fault
          without it. */}
      {started && (
        <p className="home-note">
          Recording twice in one day counts as one baseline day. That is why these two
          numbers can differ.
        </p>
      )}
    </Card>
  )
}

/**
 * History exists and nothing in it produced a result.
 *
 * The two sentences never collapse into one (§4.5): "none of your recordings"
 * and "none of the ones read so far" are different claims, and only one of them
 * is true when a cursor is still outstanding. It repeats no outcome rows and
 * carries no Record link — Recent recordings directly below lists the same rows
 * with their pills, and the digest carries the one link (D5).
 */
function UnscoredHistory({ count, exhaustive }: { count: number; exhaustive: boolean }) {
  return (
    <Card>
      <div className="product-empty">
        <h2 className="product-empty__title">No scored result yet</h2>
        <p className="product-empty__body">
          {exhaustive
            ? `You have ${count} recording${count === 1 ? '' : 's'}, and none of them produced a result.`
            : `None of the ${count} most recently uploaded recordings produced a result. Older pages of your history have not been searched.`}
        </p>
      </div>
    </Card>
  )
}

/* ── The mirror (Sprint 1.9.9) ────────────────────────────────────────────── */

/**
 * Which of the mirror's three compositions this is. First run needs BOTH the
 * summary and the list to be empty (critique #13): `/home` is eyes-closed only,
 * so an account with only eyes-open recordings has an empty summary and a
 * history, and it is learning, not new.
 */
type MirrorPrimary = 'first_run' | 'learning' | 'confirmed'

interface MirrorView {
  primary: MirrorPrimary
  /**
   * 1.9.9 P3, D21: the map card is locked while the eyes-closed baseline is not
   * confirmed — "confirmed" in R37's words, a baseline with sessions whose flag
   * is `false`. From `/home.baseline.EC`, never from the reading's own
   * `baseline_status`, which can still say provisional on the confirming day.
   */
  locked: boolean
  /** The reading is from an earlier day than the server's today. */
  dimmed: boolean
  banner: { eyebrow: string | null; sentence: string; action: ReactNode } | null
  /** The map card, or null when the reading has no `state_map` (flags off): the note stands alone. */
  map: MirrorMap | null
  /** The reading in words, beside the map; null with no state map. */
  words: MirrorWords | null
  note: { dated: string; headline: string | null; body: string | null; stored: boolean; source: NoteSource | null; notice: ReactNode } | null
  chips: readonly ChipView[]
  ring: { state: 'confirmed' | 'held' | 'none'; condition: string; title: string; detail: string | null; other: string; device: string }
  next: { text: string | null; secondary: string | null } | null
  week: readonly WeekDay[]
  disclaimer: string | null
  /**
   * R73: what the payload marks research only on this screen — the state map —
   * for the notes at the end of the page. The map's word carries the marker.
   */
  research: { token: string; validated: boolean | null } | null
}

/**
 * One baseline in R37's words: "None yet", "Confirmed · 14 separate days", or
 * "Held low · 2 separate days · 1 more separate day to a confirmed baseline" —
 * the digest's and Baseline's own phrases, so one fact reads one way. Every
 * number is the endpoint's: `n_distinct_days`, and `days_confirmed_remaining`
 * rather than a count the client could only get by knowing the threshold.
 */
function baselineWords(b: BaselineStatus): { state: 'confirmed' | 'held' | 'none'; title: string; detail: string | null } {
  const n = b.n_distinct_days
  const days = `${n} separate ${n === 1 ? 'day' : 'days'}`
  if (b.low_confidence_flag === false) return { state: 'confirmed', title: 'Confirmed', detail: days }
  if (b.low_confidence_flag !== true) return { state: 'none', title: 'Unavailable', detail: null }
  const r = b.days_confirmed_remaining
  const more = r === 0 ? '' : ` · ${r} more separate ${r === 1 ? 'day' : 'days'} to a confirmed baseline`
  return { state: 'held', title: 'Building', detail: `${days}${more}` }
}

interface MirrorMap {
  lean: FieldLean
  clip: readonly [number, number] | null
  dz: number | null
  dot: MapPoint | null
  dotToken: QuadrantToken | null
  trail: readonly MapPoint[]
  corners: { tl: string; tr: string; bl: string; br: string } | null
  usual: string | null
  lock: { pill: ReactNode; caption: string } | null
  /** "today" beside the dot, when the reading's day is the server's today. */
  dotLabel: string | null
}

interface MirrorWords {
  token: QuadrantToken | null
  word: string | null
  line: string | null
  badge: ReactNode
  pill: ReactNode
}


/**
 * The mirror's view of one summary. Lookups, `===`, `??` and nothing else — no
 * comparison `clientScience` would have to be told about, because none is
 * needed: every judgement here was the backend's or the package's.
 */
function mirrorOf(summary: HomeSummaryView, pending: PendingJob | null, firstRun: boolean): MirrorView {
  const ec = summary.baseline.EC
  const locked = !(ec.n_sessions !== 0 && ec.low_confidence_flag === false)
  const empty = summary.latest === null && summary.reading === null && summary.processing === null && pending === null
  const primary: MirrorPrimary = empty && firstRun ? 'first_run' : locked ? 'learning' : 'confirmed'

  const reading = summary.reading
  const result = reading?.result ?? null
  const readable = result !== null && result.readable ? result : null

  const banner = pending !== null
    ? {
      eyebrow: 'Upload started',
      sentence: pendingSentence(pending),
      action: (
        <Link className="btn btn--ghost btn--sm" to="/record">
          Open Record
        </Link>
      ),
    }
    : summary.processing !== null
      ? { eyebrow: null, sentence: 'A recording is being processed.', action: null }
      : null

  // THE MAP, looked up (§1.4). Its numbers are the reading's own state map,
  // its corners and geometry the reading's display block first and
  // `/home.display` second, and the lock (P3, D21) hides every part of it that
  // would say where this reading is: the dot, the trail, the lean, the word,
  // the line and the pill.
  const sm = mirrorStateMap(readable, summary.display)
  const clip = sm?.clip ?? summary.display?.axisClip ?? null
  const shown = sm !== null || reading === null
  const remaining = ec.days_confirmed_remaining
  const map: MirrorMap | null = !shown
    ? null
    : {
      lean: locked ? null : sm?.quadrant ?? null,
      clip,
      dz: sm?.dz ?? summary.display?.deadzoneZ ?? null,
      dot: !locked && clip !== null && sm !== null && sm.act !== null && sm.bal !== null
        ? { act: sm.act, bal: sm.bal }
        : null,
      dotToken: locked ? null : sm?.quadrant ?? null,
      trail: locked
        ? []
        : summary.trail.flatMap((p) =>
          p.sessionDate !== reading?.sessionDate && p.activation !== null && p.balance !== null
            ? [{ act: p.activation, bal: p.balance }]
            : []),
      corners: cornerWords(sm?.corners ?? summary.display?.quadrants ?? null),
      usual: sm?.usual ?? summary.display?.usual ?? null,
      // Interface date copy, by string equality of two calendar dates; lower
      // case, and set in capitals by the stylesheet, like the corner words.
      // Not on a near-usual reading, whose dot sits on the ring and its label.
      dotLabel: !locked && sm?.nearUsual !== true && reading !== null && reading.sessionDate === summary.today ? 'today' : null,
      // The countdown is `days_confirmed_remaining`, verbatim (R50): no client
      // threshold, and nothing when the field is not there to say.
      lock: primary !== 'learning'
        ? null
        : {
          pill: remaining === 0 ? null : <Pill>{`${remaining} ${remaining === 1 ? 'day' : 'days'} to go`}</Pill>,
          caption: 'The map unlocks when your eyes closed baseline is confirmed.',
        },
    }

  const words: MirrorWords | null = sm === null
    ? null
    : {
      token: locked ? null : sm.quadrant,
      word: locked ? null : sm.word,
      line: locked ? null : sm.line,
      // D-M1: the mark stays while `validated` is false, on a dimmed reading too.
      // R73: a small marker here, and the words at the end of the page.
      // A marker beside nothing is a stray glyph: while the map is locked there is no word to mark.
      badge: locked || sm.validated === true || sm.badge === null ? null : <ResearchMarker />,
      pill: !locked && readable !== null && readable.lowConfidence
        ? <ConfidencePill confidence={readable.confidence} />
        : null,
    }

  const storedNote = reading !== null && summary.note?.recordingId === reading.recordingId
    && summary.note.headline.trim() !== '' && summary.note.body.trim() !== '' ? summary.note : null
  const note = reading === null
    ? null
    : {
      dated: `${formatSessionDate(reading.sessionDate)} · ${CONDITION_WORDS[summary.condition]}`,
      headline: readable !== null && storedNote !== null ? storedNote.headline : null,
      body: readable === null ? null : storedNote !== null ? storedNote.body : readable.summary,
      stored: readable !== null && storedNote !== null,
      // Who wrote it picks the disclosure's words (R65); null says nothing about how.
      source: readable !== null && storedNote !== null ? storedNote.source : null,
      notice: result !== null && !result.readable ? <Notice tone="warn">{result.detail}</Notice> : null,
    }

  // The ring: eyes closed, and eyes open on its own line (never merged, §5.1).
  const ecWords = baselineWords(ec)
  const eoWords = baselineWords(summary.baseline.EO)
  const ring = {
    ...ecWords,
    condition: CONDITION_WORDS.EC,
    other: `${CONDITION_WORDS.EO} · ${eoWords.title}${eoWords.detail === null ? '' : ` · ${eoWords.detail}`}`,
    device: DEVICE_LABELS[summary.baseline.deviceId],
  }

  // The week: a day's corner is named by the dictionary, `/home.display`
  // first and the reading's own second; a corner nobody can name is shown as
  // "recorded", in the neutral ink, rather than as a colour with no word.
  const dictionary = summary.display?.quadrants ?? sm?.corners ?? null
  const week: WeekDay[] = summary.week.map((day) => {
    const word = day.quadrant !== null && dictionary !== null && Object.hasOwn(dictionary, day.quadrant)
      ? dictionary[day.quadrant]
      : null
    const kind = !day.recorded ? 'hollow' : word !== null ? 'quadrant' : 'neutral'
    return {
      date: day.date,
      label: formatWeekdayShort(day.date) ?? day.date,
      name: `${formatSessionDate(day.date)}: ${word ?? (day.recorded ? 'recorded' : 'not recorded')}`,
      kind,
      token: kind === 'quadrant' ? day.quadrant : null,
      today: day.date === summary.today,
    }
  })

  const hint = summary.hint?.text ?? null
  const secondary = summary.secondaryHint?.text ?? null
  const due = primary === 'confirmed' && reading !== null && reading.sessionDate !== summary.today
    && pending === null && summary.processing === null

  return {
    primary,
    locked,
    dimmed: reading !== null && reading.sessionDate !== summary.today,
    banner,
    map,
    words,
    note,
    chips: readable === null ? [] : mirrorChips(reading),
    ring,
    // First run is the map, the action and the guide, as the concept's frame
    // draws it (fidelity #30): no ring, no hint, no week.
    next: primary === 'first_run' || (!due && hint === null && secondary === null) ? null : { text: hint, secondary },
    week,
    disclaimer: readable?.disclaimer ?? null,
    research: sm === null || sm.validated === true || sm.badge === null
      ? null
      : { token: sm.badge, validated: sm.validated },
  }
}

/**
 * The mirror's top section, then the 1.9.6 Home as "Your overview", then the
 * disclaimer behind "About these readings" (D32). The trust line stays where it
 * was, below everything (R38).
 */
function Mirror({
  summary,
  pending,
  firstRun,
  children,
}: {
  summary: HomeSummaryView
  pending: PendingJob | null
  firstRun: boolean
  children: ReactNode
}) {
  const { resolved } = useTheme()
  const view = mirrorOf(summary, pending, firstRun)
  // `data-painted` names the theme the field was last PAINTED for, set by the
  // paint itself (critique #18); with no field, there is nothing to wait for.
  const [painted, setPainted] = useState<ResolvedTheme | null>(null)
  const paintedFor = view.map === null ? resolved : painted
  // "Yesterday", relative to the SERVER'S today (critique #20), so the dimming
  // and its label agree about which day it is.
  const serverToday = localNoonOf(summary.today)
  const when = view.dimmed && serverToday !== null && summary.reading !== null
    ? formatRelativeDay(summary.reading.sessionDate, serverToday)
    : null

  // "Yesterday", compact, only when there is a map and a word to put in it.
  // Not while the map is locked: a lock pill in a 120px field is no card at all.
  const compact = view.dimmed && !view.locked && view.map !== null && view.words !== null
  // A secondary button, its arrow hidden from the name (fidelity #14).
  const fullReport = (
    <>
      <Link className="btn btn--secondary btn--sm" to="/report" data-testid="home-full-report">
        See the full report
        <svg className="mirror__arrow" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      </Link>
      {/* agent-1-web: on the card a normal Home shows -- not only on the
          fallback LatestStatus (W4/W5 review). Behind the module-scope flag. */}
      {ASSISTANT_ENABLED && <AskAboutThis />}
    </>
  )

  return (
    <>
      {view.banner !== null && <ProcessingBanner {...view.banner} />}
      <section
        className="mirror subgrid"
        data-testid="home-mirror"
        data-mirror-state={view.primary}
        data-painted={paintedFor ?? undefined}
      >
        {/* Learning leads with the baseline, as the concept's frame draws it
            (fidelity #32): the ring is what is happening, the map is locked. */}
        {view.primary === 'learning' && <BaselineRing {...view.ring} />}
        {view.primary === 'confirmed' && view.dimmed && view.banner === null && view.next !== null && (
          <NextReading {...view.next} action={<TakeReading />} />
        )}
        <div className={view.dimmed ? 'mirror__hero mirror__reading--dimmed' : 'mirror__hero'}>
          {(view.map !== null || view.note !== null) && (
            // The map and the note are ONE card, map beside note (fidelity #7);
            // with flags off the note is the card alone. An earlier day's reading
            // is the concept's compact "yesterday" card instead (#40): a small
            // field, the word and the way to the report.
            <Card
              as="article"
              className={compact
                ? 'mirror__card mirror__card--compact'
                : view.map !== null && view.note !== null ? 'mirror__card mirror__card--pair grid-12' : 'mirror__card'}
              active={!view.dimmed}
              data-testid="home-reading"
            >
              {/* An older reading says so, once, at the head of its card: the
                  word under the map is in the present tense. */}
              {when !== null && view.note !== null && (
                <p className="mirror__dated">{`From ${when} · ${view.note.dated}`}</p>
              )}
              {compact && view.map !== null && view.words !== null ? (
                <div className="mirror__compact">
                  <div className="mirror__map">
                    <QuadrantMap
                      lean={view.map.lean}
                      clip={view.map.clip}
                      dz={view.map.dz}
                      dot={view.map.dot}
                      dotToken={view.map.dotToken}
                      // The compact previous reading shows its dot alone.
                      trail={[]}
                      corners={null}
                      usualLabel={null}
                      lock={view.map.lock}
                      onPainted={setPainted}
                    />
                  </div>
                  {/* The word and the note's text, as the concept's quote; no
                      line, no chips, no corner words on a field this small.
                      No note headline either (R69): the word leads this card,
                      and the template's headline is that word again. */}
                  <NoteCard
                    dated={null}
                    headline={null}
                    body={view.note?.body ?? null}
                    stored={view.note?.stored ?? false}
                    source={view.note?.source ?? null}
                    compact
                    lead={<QuadrantWord {...view.words} line={null} />}
                    action={fullReport}
                  />
                </div>
              ) : (
                <>
                  {view.map !== null && (
                    <div className="mirror__map">
                      <QuadrantMap
                        lean={view.map.lean}
                        clip={view.map.clip}
                        dz={view.map.dz}
                        dot={view.map.dot}
                        dotToken={view.map.dotToken}
                        trail={view.map.trail}
                        corners={view.map.corners}
                        usualLabel={view.map.usual}
                        lock={view.map.lock}
                        dotLabel={view.map.dotLabel}
                        onPainted={setPainted}
                      />
                      {view.words !== null && <QuadrantWord {...view.words} />}
                    </div>
                  )}
                  {view.note !== null && (
                    <NoteCard
                      dated={when === null ? view.note.dated : null}
                      headline={view.note.headline}
                      body={view.note.body}
                      stored={view.note.stored}
                      source={view.note.source}
                      notice={view.note.notice}
                      chips={<WhatMoved chips={view.chips} />}
                      action={fullReport}
                    />
                  )}
                </>
              )}
            </Card>
          )}
        </div>
        <div className="mirror__row grid-row">
          {view.primary !== 'learning' && view.primary !== 'first_run' && <BaselineRing {...view.ring} />}
          {view.next !== null && !(view.primary === 'confirmed' && view.dimmed && view.banner === null) && <NextReading {...view.next} />}
          {view.primary !== 'first_run' && <LastSevenDays days={view.week} />}
        </div>
      </section>
      <Overview>{children}</Overview>
      {view.disclaimer !== null && <AboutReadings disclaimer={view.disclaimer} />}
      {view.research !== null && <HomeResearchNotes {...view.research} />}
    </>
  )
}

/**
 * R73: Home's research notes, at the end of the page. They say what the
 * payload marks — the state map's `badge` and `validated` — and nothing more.
 * A token this client does not know is shown as itself, as ResearchBadge did.
 */
function HomeResearchNotes({ token, validated }: { token: string; validated: boolean | null }) {
  return (
    <ResearchNotes testid="home-research-notes">
      <p className="research-notes__item">
        {token === 'research_only' ? (
          <>The state map, with its word and this reading’s place on it, is marked research only in this reading{validated === false ? ': it is not validated.' : '.'}</>
        ) : (
          <>The state map, with its word and this reading’s place on it, carries the mark <Tag>{token}</Tag> in this reading{validated === false ? ': it is not validated.' : '.'}</>
        )}
      </p>
    </ResearchNotes>
  )
}
